/**
 * Sprint BG Part 3 — ja / en 辞書 (主要 50 文字列の minimum)。
 *
 * - ja は v2 既存 UI の hardcode wording に合わせる
 * - en は機械翻訳ベース (人間レビュー前、Phase 6+ で品質改善予定)
 * - key が dict に無ければ t() は key 文字列をそのまま返す (graceful fallback)
 */

import type { I18nKey, Locale } from './keys';

type Dictionary = Record<I18nKey, string>;

const ja: Dictionary = {
  'common.ok': 'OK',
  'common.cancel': 'キャンセル',
  'common.save': '保存',
  'common.delete': '削除',
  'common.edit': '編集',
  'common.close': '閉じる',
  'common.back': '戻る',
  'common.next': '次へ',
  'common.loading': '読み込み中…',
  'common.retry': '再試行',
  'common.required': '必須',
  'common.optional': '任意',

  'auth.signin_title': 'サインイン',
  'auth.signin_button': 'サインイン',
  'auth.signout_button': 'サインアウト',
  'auth.email_label': 'メールアドレス',
  'auth.password_label': 'パスワード',
  'auth.signin_failed': 'サインインに失敗しました',
  'auth.nickname_label': 'ニックネーム',
  'auth.nickname_placeholder': '例: 山田太郎',

  'post.create_title': '新規投稿',
  'post.title_label': 'タイトル',
  'post.title_placeholder': '例: 駅前の歩道が狭い',
  'post.comment_label': 'コメント',
  'post.submit_button': '投稿する',
  'post.submit_pending': '投稿中…',
  'post.location_picker_hint': '地図を長押しして位置を指定',
  'post.photo_capture_button': '📷 撮影',
  'post.photo_gallery_button': '🖼 ギャラリーから選ぶ',
  'post.delete_confirm': 'この投稿を削除してよろしいですか？',

  'analysis.tab_list': '一覧',
  'analysis.tab_matrix': 'マトリクス',
  'analysis.tab_heatmap': 'ヒートマップ',
  'analysis.tab_network': 'ネットワーク',
  'analysis.tab_score': 'スコア',
  'analysis.tab_insight': '気づき',
  'analysis.tab_idea': '処方箋',
  'analysis.no_posts': '投稿がありません',

  'settings.title': '設定',
  'settings.reset_section': '設定をリセット',
  'settings.reset_confirm': '設定をリセットしてよろしいですか？',

  'error.network': '通信エラーが発生しました',
  'error.unknown': '不明なエラーが発生しました',
  'error.permission_denied': '権限がありません',
  'error.not_found': '見つかりませんでした',
  'error.rate_limit': '操作が多すぎます。しばらく待ってから再試行してください',

  'offline.indicator': 'オフライン',
  'offline.queued': '未送信',
  'offline.flush_pending': '送信を再試行中…',

  'a11y.menu_open': 'メニューを開く',
  'a11y.modal_close': 'モーダルを閉じる',

  // offline_map.* (AutoMapDialog、Sprint BG-DataSettings 19 / U-C13)
  'offline_map.auto_dialog.title': '{districtName} のオフラインマップを保存しますか？',
  'offline_map.auto_dialog.intro':
    '通信状況に左右されずに地図を表示するため、事前に端末へ保存することをおすすめします。',
  'offline_map.auto_dialog.size_label': 'サイズ',
  'offline_map.auto_dialog.wifi_only': 'Wi-Fi のときのみ保存する',
  'offline_map.auto_dialog.confirm': '今すぐ保存',
  'offline_map.auto_dialog.dismiss': 'あとで',
  'offline_map.auto_dialog.dont_ask_again': '今後表示しない',
  'offline_map.auto_dialog.busy_saving': '保存中…',
  'offline_map.auto_dialog.busy_progress': 'ダウンロード中… {loaded} / {total} MB',
  'offline_map.auto_dialog.error_wifi_required': 'Wi-Fi に接続してから再度お試しください。',

  // district_home.*
  'district_home.title': 'まちあるきマップ',
  'district_home.subtitle': 'まちを歩いて気づきを地図に残すウェブアプリ',
  'district_home.your_districts': 'あなたの参加地区',
  'district_home.open_district': '地区を開く',
  'district_home.no_memberships_hint':
    'まだどの地区にも参加していません。地区主催者から QR / URL を受け取ってアクセスすると、ここに一覧が表示されます。',

  // drawer.*
  'drawer.menu_aria': 'メニュー',
  'drawer.close': 'メニューを閉じる',
  'drawer.home_districts': '参加地区一覧',
  'drawer.posts_app': '投稿アプリへ戻る',
  'drawer.analysis_app': '分析アプリへ',
  'drawer.offline_map': 'オフラインマップ',
  'drawer.announcements': 'お知らせ履歴',
  // 2026-09-05 オーナー指示: メニュー表記は「アンケート一覧」ではなく「アンケート」。
  'drawer.surveys': 'アンケート',
  'drawer.verification': 'AI 判定をチェック',
  'drawer.guide': '使い方ガイド',
  'drawer.recovery_code': 'ユーザー復帰コードの表示',
  'drawer.recover_from_other': 'バックアップから戻す',
  'drawer.device_transfer': 'PWA に引き継ぐ',
  'drawer.settings': 'ユーザー設定',
  'drawer.section.handoff': 'データの引き継ぎ',
  'drawer.handoff_help.backup':
    '機種変更や端末紛失に備えて、別端末でも続けられる番号を控えておきます。',
  'drawer.handoff_help.restore': '別端末で控えた番号を入力して、この端末で続きを始めます。',
  'drawer.handoff_help.to_home': 'ブラウザの参加内容を、ホーム画面に追加したアプリへ移します。',
  'drawer.recover_input': 'ユーザー復帰コードの入力',
  'drawer.ios_standalone_warning':
    '※iOS は「ホーム画面に追加」しない場合、7 日間アプリを開かないと自分のアカウントが消えてしまいます。',

  // app_home.*
  'app_home.offline_badge': 'オフライン',
  'app_home.outbox_badge': '未送信 {count}',
  'app_home.ecw_outbox_badge': '気づき/処方箋 {count}',

  // install_prompt.* (Phase H, old2 user.json#installPrompt 逐語)
  'install_prompt.title': 'ホーム画面に追加すると便利です',
  'install_prompt.ios_benefit':
    'iPhone は「ホーム画面に追加」しないと、7日間アプリを開かないと自分のアカウントが消えてしまいます。',
  'install_prompt.android_benefit':
    'ホーム画面に追加すると、地図やデータが消えにくくなり、アプリのように起動できます。',
  'install_prompt.ios_steps':
    '下の「共有ボタン」（□から↑が出ているマーク）をタップ → 「ホーム画面に追加」を選んでください。',
  'install_prompt.android_install': 'ホーム画面に追加',
  'install_prompt.later': 'あとで',
  'install_prompt.dont_show_again': '今後表示しない',
};

// 機械翻訳ベース (Phase 6+ で人間レビュー予定)。
const en: Dictionary = {
  'common.ok': 'OK',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.required': 'required',
  'common.optional': 'optional',

  'auth.signin_title': 'Sign in',
  'auth.signin_button': 'Sign in',
  'auth.signout_button': 'Sign out',
  'auth.email_label': 'Email',
  'auth.password_label': 'Password',
  'auth.signin_failed': 'Sign-in failed',
  'auth.nickname_label': 'Nickname',
  'auth.nickname_placeholder': 'e.g. Taro Yamada',

  'post.create_title': 'New post',
  'post.title_label': 'Title',
  'post.title_placeholder': 'e.g. Sidewalk near the station is narrow',
  'post.comment_label': 'Comment',
  'post.submit_button': 'Post',
  'post.submit_pending': 'Posting…',
  'post.location_picker_hint': 'Long-press the map to set the location',
  'post.photo_capture_button': '📷 Take photo',
  'post.photo_gallery_button': '🖼 Choose from gallery',
  'post.delete_confirm': 'Delete this post?',

  'analysis.tab_list': 'List',
  'analysis.tab_matrix': 'Matrix',
  'analysis.tab_heatmap': 'Heatmap',
  'analysis.tab_network': 'Network',
  'analysis.tab_score': 'Score',
  'analysis.tab_insight': 'Insight',
  'analysis.tab_idea': 'Idea',
  'analysis.no_posts': 'No posts',

  'settings.title': 'Settings',
  'settings.reset_section': 'Reset settings',
  'settings.reset_confirm': 'Reset settings?',

  'error.network': 'Network error',
  'error.unknown': 'Unknown error',
  'error.permission_denied': 'Permission denied',
  'error.not_found': 'Not found',
  'error.rate_limit': 'Too many operations. Please wait and retry.',

  'offline.indicator': 'Offline',
  'offline.queued': 'Queued',
  'offline.flush_pending': 'Retrying upload…',

  'a11y.menu_open': 'Open menu',
  'a11y.modal_close': 'Close modal',

  // offline_map.* (AutoMapDialog, Sprint BG-DataSettings 19 / U-C13)
  'offline_map.auto_dialog.title': 'Save offline map for {districtName}?',
  'offline_map.auto_dialog.intro':
    'Saving the map to your device lets you view it even when your connection is unstable.',
  'offline_map.auto_dialog.size_label': 'Size',
  'offline_map.auto_dialog.wifi_only': 'Only save over Wi-Fi',
  'offline_map.auto_dialog.confirm': 'Save now',
  'offline_map.auto_dialog.dismiss': 'Later',
  'offline_map.auto_dialog.dont_ask_again': 'Do not ask again',
  'offline_map.auto_dialog.busy_saving': 'Saving…',
  'offline_map.auto_dialog.busy_progress': 'Downloading… {loaded} / {total} MB',
  'offline_map.auto_dialog.error_wifi_required': 'Please connect to Wi-Fi and try again.',

  // district_home.*
  'district_home.title': 'CitizenWalk',
  'district_home.subtitle': 'Walk around town and leave your discoveries on the map.',
  'district_home.your_districts': 'Your districts',
  'district_home.open_district': 'Open district',
  'district_home.no_memberships_hint':
    'You have not joined any district yet. Scan the QR code or open the URL from the host to see them here.',

  // drawer.*
  'drawer.menu_aria': 'Menu',
  'drawer.close': 'Close menu',
  'drawer.home_districts': 'My districts',
  'drawer.posts_app': 'Back to posts',
  'drawer.analysis_app': 'Open analysis app',
  'drawer.offline_map': 'Offline map',
  'drawer.announcements': 'Announcements',
  'drawer.surveys': 'Surveys',
  'drawer.verification': 'Check AI judgement',
  'drawer.guide': 'User guide',
  'drawer.recovery_code': 'Show user recovery code',
  'drawer.recover_from_other': 'Restore from backup code',
  'drawer.device_transfer': 'Hand off to PWA',
  'drawer.settings': 'User settings',
  'drawer.section.handoff': 'Data handoff',
  'drawer.handoff_help.backup': 'Save a code so you can resume on another device if needed.',
  'drawer.handoff_help.restore': 'Enter a code from another device to resume here.',
  'drawer.handoff_help.to_home': 'Move your browser session to the home-screen app.',
  'drawer.recover_input': 'Enter user recovery code',
  'drawer.ios_standalone_warning':
    'On iOS, if you do not "Add to Home Screen", your account is removed after 7 days without opening the app.',

  // app_home.*
  'app_home.offline_badge': 'Offline',
  'app_home.outbox_badge': 'Queued {count}',
  'app_home.ecw_outbox_badge': 'Insights/Ideas {count}',

  // install_prompt.* (machine translation; ja is the source of truth from old2)
  'install_prompt.title': 'Adding to your home screen is handy',
  'install_prompt.ios_benefit':
    'On iPhone, unless you "Add to Home Screen", your account is removed if you do not open the app for 7 days.',
  'install_prompt.android_benefit':
    'Adding to the home screen makes maps and data less likely to be lost, and lets you launch it like an app.',
  'install_prompt.ios_steps':
    'Tap the Share button below (the box with an upward arrow) → choose "Add to Home Screen".',
  'install_prompt.android_install': 'Add to Home Screen',
  'install_prompt.later': 'Later',
  'install_prompt.dont_show_again': 'Do not show again',
};

export const DICTIONARIES: Record<Locale, Dictionary> = { ja, en };
